/**
 * Event Call to Action Hooks
 * React Query hooks for managing event CTAs
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { UUID } from "../types/common.types";
import type {
  Event,
  CreateEventCTAInput,
  UpdateEventCTAInput,
} from "../types/event.types";
import {
  getEventCTAs,
  getEventCTA,
  createEventCTA,
  updateEventCTA,
  deleteEventCTA,
  generateCTAQRCode,
} from "../services/eventCTA.service";
import { useAuth } from "../contexts/AuthContext";

// Query keys
export const eventCTAKeys = {
  all: ["eventCTAs"] as const,
  list: (eventId: UUID) => [...eventCTAKeys.all, "list", eventId] as const,
  detail: (ctaId: UUID) => [...eventCTAKeys.all, "detail", ctaId] as const,
};

/**
 * Hook to get all CTAs for an event
 */
export function useEventCTAs(eventId: UUID | undefined) {
  return useQuery({
    queryKey: eventCTAKeys.list(eventId!),
    queryFn: () => getEventCTAs(eventId!),
    enabled: !!eventId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to get a single CTA
 */
export function useEventCTA(ctaId: UUID | undefined) {
  return useQuery({
    queryKey: eventCTAKeys.detail(ctaId!),
    queryFn: () => getEventCTA(ctaId!),
    enabled: !!ctaId,
    staleTime: 30000,
  });
}

/**
 * Hook to create a new CTA
 */
export function useCreateEventCTA() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (input: CreateEventCTAInput) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return createEventCTA(input, user.id);
    },
    onSuccess: (data) => {
      // Invalidate CTAs list for this event
      queryClient.invalidateQueries({
        queryKey: eventCTAKeys.list(data.event_id),
      });
    },
  });
}

/**
 * Hook to update an existing CTA
 */
export function useUpdateEventCTA() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({
      ctaId,
      updates,
    }: {
      ctaId: UUID;
      updates: UpdateEventCTAInput;
    }) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return updateEventCTA(ctaId, updates, user.id);
    },
    onSuccess: (data) => {
      // Invalidate CTAs list for this event
      queryClient.invalidateQueries({
        queryKey: eventCTAKeys.list(data.event_id),
      });
      // Invalidate this specific CTA
      queryClient.invalidateQueries({
        queryKey: eventCTAKeys.detail(data.id),
      });
    },
  });
}

/**
 * Hook to delete a CTA
 */
export function useDeleteEventCTA() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ctaId }: { ctaId: UUID; eventId: UUID }) =>
      deleteEventCTA(ctaId),
    onSuccess: (_, variables) => {
      // Invalidate CTAs list for this event
      queryClient.invalidateQueries({
        queryKey: eventCTAKeys.list(variables.eventId),
      });
    },
  });
}

/**
 * Hook to generate/regenerate QR code for a CTA
 */
export function useGenerateCTAQRCode() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: ({ ctaId, event }: { ctaId: UUID; event: Event }) => {
      if (!user?.id) {
        throw new Error("User not authenticated");
      }
      return generateCTAQRCode(ctaId, event, user.id);
    },
    onSuccess: (data) => {
      // Invalidate CTAs list for this event
      queryClient.invalidateQueries({
        queryKey: eventCTAKeys.list(data.event_id),
      });
      // Invalidate this specific CTA
      queryClient.invalidateQueries({
        queryKey: eventCTAKeys.detail(data.id),
      });
    },
  });
}
